import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import httpBuildQuery from 'http-build-query';
import {
  Card,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableSortLabel,
  TableRow
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { BackdropCircular } from 'components';
import {
  getStoreCustomersGridApi,
  getStoreCustomerGroupsApi
} from 'api';
import useLocalStorageTableCells from 'hooks/useLocalStorageTableCells';
import { parseSearchParams } from 'helpers/customers';
import CustomTableToolbar from './CustomTableToolbar';
import CustomerRow from './CustomerRow';
import FilterDrawer from './FilterDrawer';
import { useStyles } from './styles';


const defaultTableCells = [
  { sortField: 'entity_id', label: 'ID', display: false },
  { sortField: 'name', label: 'Name', display: true },
  { sortField: 'email', label: 'Email', display: true },
  { sortField: 'practice_name', label: 'Company Name', display: true },
  { sortField: 'group_id', label: 'Customer Group', display: true },
  { sortField: 'created_at', label: 'Customer Since', display: true },
  { sortField: 'billing_city', label: 'Billing City', display: false },
  { sortField: 'billing_region', label: 'Billing State', display: true },
  { sortField: 'billing_postcode', label: 'Billing Zip', display: true },
  { sortField: 'billing_full', label: 'Billing Address', display: false },
  { sortField: 'billing_telephone', label: 'Phone', display: false },
  { sortField: 'created_in', label: 'Account Created In', display: false },
  { sortField: 'dob', label: 'Date of Birth', display: false },
  { sortField: 'gender', label: 'Gender', display: false },
  { label: 'Details', display: true },
];

const CustomersList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const paramsFromUrl: IParamsFromUrl = parseSearchParams(location.search);

  const [sortDirection, setSortDirection] = useState(paramsFromUrl.sortDirection || 'desc');
  const [sortField, setSortField] = useState(paramsFromUrl.sortField || 'created_at');
  const [currentPage, setCurrentPage] = useState(paramsFromUrl.currentPage || 1);
  const [rowsPerPage, setRowsPerPage] = useState(paramsFromUrl.rowsPerPage || 10);
  const [filterConditions, setFilterConditions] = useState<IFilterConditions>(paramsFromUrl.filterConditions || {});
  const [searchConditions, setSearchConditions] = useState(paramsFromUrl.searchConditions || {});
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const { headTableCells, setHeadTableCells } = useLocalStorageTableCells('customersTableCells', defaultTableCells);

  const {
    storeCustomers,
    customersTotalCount,
    loadingCustomers,
    customerGroups
  } = useSelector((state: IReduxState) => state.customers);
  const { userStoreData: {storeCode} } = useSelector((state: IReduxState) => state.user);
  const isCustomer3 = storeCode === 'customer3';

  useEffect(() => {
    getStoreCustomersGridApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);

  useEffect(() => {
    const queryParams = {
      filterConditions,
      searchConditions,
      currentPage,
      sortDirection,
      sortField,
      rowsPerPage,
    };

    const queryString = httpBuildQuery(queryParams);
    history.replace({
      pathname: location.pathname,
      search: queryString,
    });
  }, [filterConditions, searchConditions, currentPage, sortDirection, sortField, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  useEffect(() => {
    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }
  }, []);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const onSearch = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  return (
    <div>
      <Card className="mx-10">
        <div className={`py-5 ${classes.toolbarContainer}`}>
          <CustomTableToolbar
            onSearch={onSearch}
            setOpenFilter={setOpenFilter}
            filterConditions={filterConditions}
            tableCells={headTableCells}
            setHeadTableCells={setHeadTableCells}
          />
        </div>
        <div className="table-container">
          <TableContainer>
            <Table aria-label="table" className={classes.table}>
              <TableHead>
                <TableRow>
                  {headTableCells.map((cell, index) => {
                    if (!cell.display || (cell.sortField === 'practice_name' && !isCustomer3)) {
                      return null;
                    }

                    return (
                      <TableCell key={index} align={cell.label === 'Details' ? 'center' : 'left'}>
                        {cell.sortField ? (
                          <TableSortLabel
                            active={sortField === cell.sortField}
                            IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                            onClick={(event) => handleRequestSort(event, cell.sortField)}
                          >
                            {cell.label}
                          </TableSortLabel>
                        ) : (
                          cell.label
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {!storeCustomers?.length ?
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No matching customers found.
                    </TableCell>
                  </TableRow>
                  :
                  <>
                    {storeCustomers.map((customerData) => {
                      return (
                        <CustomerRow
                          key={customerData.entity_id}
                          customerData={customerData}
                          headTableCells={headTableCells}
                        />
                      );
                    })}
                  </>
                }
              </TableBody>
            </Table>
          </TableContainer>
          {!!customersTotalCount &&
            <TablePagination
              rowsPerPageOptions={[10, 20, 40, 80, 100]}
              component="div"
              className={classes.paginationContainer}
              count={customersTotalCount}
              rowsPerPage={Number(rowsPerPage)}
              page={Number(currentPage) - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }
        </div>
      </Card>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <FilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
        />
      </Drawer>
      <BackdropCircular loading={loadingCustomers} />
    </div>
  );
};

export default CustomersList;
